import { WebAuthnAbortService } from "@simplewebauthn/browser";

const CEREMONY_TIMEOUT_MS = 90_000;

export class CeremonyCanceledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotAllowedError";
  }
}

const ceremonyOwnerTag: unique symbol = Symbol("CeremonyOwner");

export interface CeremonyOwner {
  readonly [ceremonyOwnerTag]: true;
}

export interface GuardedCeremonyOptions {
  owner: CeremonyOwner;
  timeoutMs?: number;
}

interface LiveCeremony {
  owner: CeremonyOwner;
  stop: (reason: CeremonyCanceledError) => void;
}

interface PendingStart {
  owner: CeremonyOwner;
  abandon: (reason: CeremonyCanceledError) => void;
}

const releasedOwners = new WeakSet<CeremonyOwner>();
const liveCeremonies: LiveCeremony[] = [];
const pendingStarts: PendingStart[] = [];
const ceremonyListeners = new Set<() => void>();

function notifyCeremonyListeners(): void {
  for (const listener of ceremonyListeners) listener();
}

function holdsBrowserPrompt(ceremony: LiveCeremony): boolean {
  return liveCeremonies[liveCeremonies.length - 1] === ceremony;
}

export function createCeremonyOwner(): CeremonyOwner {
  return { [ceremonyOwnerTag]: true };
}

export function isCeremonyActive(owner?: CeremonyOwner): boolean {
  if (owner === undefined) return liveCeremonies.length > 0;
  return liveCeremonies.some((ceremony) => ceremony.owner === owner);
}

export function subscribeCeremonyActivity(listener: () => void): () => void {
  ceremonyListeners.add(listener);
  return () => {
    ceremonyListeners.delete(listener);
  };
}

export function guardedCeremony<T>(
  startCeremony: () => Promise<T>,
  { owner, timeoutMs = CEREMONY_TIMEOUT_MS }: GuardedCeremonyOptions,
): Promise<T> {
  if (releasedOwners.has(owner)) {
    return Promise.reject(
      new CeremonyCanceledError("The passkey prompt was abandoned before it opened."),
    );
  }

  let timer: ReturnType<typeof setTimeout>;
  let settle: (err: CeremonyCanceledError) => void;

  const ceremony: LiveCeremony = {
    owner,
    stop: (reason) => {
      if (holdsBrowserPrompt(ceremony)) WebAuthnAbortService.cancelCeremony();
      settle(reason);
    },
  };

  const interrupt = new Promise<never>((_, reject) => {
    settle = reject;
    timer = setTimeout(() => {
      ceremony.stop(new CeremonyCanceledError("The passkey prompt timed out."));
    }, timeoutMs);
  });

  const unregister = () => {
    clearTimeout(timer);
    const index = liveCeremonies.indexOf(ceremony);
    if (index !== -1) liveCeremonies.splice(index, 1);
    notifyCeremonyListeners();
  };

  liveCeremonies.push(ceremony);
  notifyCeremonyListeners();

  let running: Promise<T>;
  try {
    running = startCeremony();
  } catch (err) {
    unregister();
    throw err;
  }

  return Promise.race([running, interrupt]).finally(unregister);
}

export function preparedCeremony<P, T>(
  prepare: (signal: AbortSignal) => Promise<P>,
  startCeremony: (prepared: P) => Promise<T>,
  options: GuardedCeremonyOptions,
): Promise<T> {
  const controller = new AbortController();
  let abandonedWith: CeremonyCanceledError | null = null;
  let settle: (reason: CeremonyCanceledError) => void;
  const abandoned = new Promise<never>((_, reject) => {
    settle = reject;
  });

  const pending: PendingStart = {
    owner: options.owner,
    abandon: (reason) => {
      abandonedWith ??= reason;
      controller.abort(reason);
      settle(reason);
    },
  };

  const unregister = () => {
    const index = pendingStarts.indexOf(pending);
    if (index !== -1) pendingStarts.splice(index, 1);
  };

  pendingStarts.push(pending);

  const started = (async () => {
    const prepared = await prepare(controller.signal);
    if (abandonedWith) throw abandonedWith;
    unregister();
    return guardedCeremony(() => startCeremony(prepared), options);
  })();

  return Promise.race([started, abandoned]).finally(unregister);
}

function stopLiveCeremonies(ceremonies: LiveCeremony[]): void {
  for (const ceremony of ceremonies) {
    ceremony.stop(new CeremonyCanceledError("The passkey prompt was canceled."));
  }
}

function abandonPendingStarts(starts: PendingStart[]): void {
  for (const start of starts) {
    start.abandon(new CeremonyCanceledError("The passkey prompt was canceled before it opened."));
  }
}

export function cancelCeremonies(owner: CeremonyOwner): void {
  abandonPendingStarts(pendingStarts.filter((start) => start.owner === owner));
  stopLiveCeremonies(liveCeremonies.filter((live) => live.owner === owner));
}

export function cancelAllCeremonies(): void {
  abandonPendingStarts(Array.from(pendingStarts));
  stopLiveCeremonies(Array.from(liveCeremonies));
}

export function releaseCeremonyOwner(owner: CeremonyOwner): void {
  releasedOwners.add(owner);
  stopLiveCeremonies(liveCeremonies.filter((live) => live.owner === owner));
}

export function isCeremonyOwnerReleased(owner: CeremonyOwner): boolean {
  return releasedOwners.has(owner);
}

export function claimCeremonyOwner(owner: CeremonyOwner): void {
  releasedOwners.delete(owner);
}

type SignalUnknownCredential = (options: { rpId: string; credentialId: string }) => Promise<void>;

export function signalUnknownRegistrationCredential(args: {
  rpId: string;
  credentialId: string;
}): void {
  if (typeof PublicKeyCredential === "undefined") return;
  const signal = (
    PublicKeyCredential as unknown as {
      signalUnknownCredential?: SignalUnknownCredential;
    }
  ).signalUnknownCredential;
  if (typeof signal !== "function") return;
  void signal
    .call(PublicKeyCredential, {
      rpId: args.rpId,
      credentialId: args.credentialId,
    })
    .catch(() => undefined);
}
